import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { EASE } from "../Reveal";
import { Receipt, Network, HeartPulse, FileSearch } from "lucide-react";

const NODES = [
  {
    key: "expense",
    label: "Business Expenses",
    sub: "Shipping · Processing",
    icon: Receipt,
    cls: "left-[26%] sm:left-[20%] top-[13%] sm:top-[15%]",
    caption: "Evaluating everyday operating expenses…",
    spark: "M0 16 L6 12 L12 14 L18 8 L24 10 L30 3",
    delay: 0.55,
  },
  {
    key: "technology",
    label: "Technology",
    sub: "Connectivity · Cloud",
    icon: Network,
    cls: "left-[74%] sm:left-[80%] top-[13%] sm:top-[15%]",
    caption: "Mapping smarter technology spending…",
    spark: "M0 14 L6 15 L12 9 L18 11 L24 5 L30 6",
    delay: 0.7,
  },
  {
    key: "benefits",
    label: "Employee Benefits",
    sub: "Healthcare · Care",
    icon: HeartPulse,
    cls: "left-[26%] sm:left-[20%] top-[87%] sm:top-[85%]",
    caption: "Exploring better care for your people…",
    spark: "M0 8 L6 11 L12 6 L18 12 L24 7 L30 9",
    delay: 0.85,
  },
  {
    key: "tax",
    label: "Tax Opportunities",
    sub: "Credits · Incentives",
    icon: FileSearch,
    cls: "left-[74%] sm:left-[80%] top-[87%] sm:top-[85%]",
    caption: "Scanning for overlooked incentives…",
    spark: "M0 15 L6 11 L12 13 L18 7 L24 9 L30 2",
    delay: 1.0,
  },
];

const PATHS = {
  expense: "M20 15 C 20 33, 24 42, 30 46",
  technology: "M80 15 C 80 33, 76 42, 70 46",
  benefits: "M20 85 C 20 67, 24 58, 30 54",
  tax: "M80 85 C 80 67, 76 58, 70 54",
};

const METERS = [
  { label: "Expense visibility", v: 72 },
  { label: "Tech efficiency", v: 64 },
  { label: "Benefits reach", v: 58 },
  { label: "Tax readiness", v: 66 },
];

const CHART_LINE =
  "M4 86 C 30 80, 48 66, 72 62 C 96 58, 112 70, 136 60 C 160 50, 176 40, 202 35 C 228 30, 258 22, 296 12";

function Sparkline({ d, active }) {
  return (
    <svg viewBox="0 0 30 18" className="w-9 h-5 shrink-0" aria-hidden="true">
      <path
        d={d}
        fill="none"
        stroke={active ? "#15B8A6" : "#8FB3D9"}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transition: "stroke .3s" }}
      />
    </svg>
  );
}

function Core() {
  const reduced = useReducedMotion();
  return (
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[56%] sm:w-[46%] z-20">
      {/* soft halo behind the core */}
      <div className="absolute -inset-8 bg-teal/25 blur-[54px] rounded-full" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
        className="relative rounded-2xl bg-white/[0.97] backdrop-blur border border-white/60 shadow-core p-4 sm:p-5"
        data-testid="engine-core"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-[0.18em] text-slate">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex w-full h-full rounded-full bg-teal opacity-60 animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-teal" />
            </span>
            Profitability Engine
          </span>
          <span className="rounded-full bg-navy text-teal-bright text-[8px] font-extrabold uppercase tracking-[0.12em] px-2.5 py-1 whitespace-nowrap">
            Your Bottom Line
          </span>
        </div>

        <svg viewBox="0 0 300 110" className="w-full h-auto mt-3" role="img" aria-label="Upward momentum chart">
          <defs>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#3D6BA8" />
              <stop offset="1" stopColor="#15B8A6" />
            </linearGradient>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#15B8A6" stopOpacity="0.22" />
              <stop offset="1" stopColor="#15B8A6" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[28, 54, 80].map((y) => (
            <line key={y} x1="4" y1={y} x2="296" y2={y} stroke="#EAF4F8" strokeWidth="1" />
          ))}
          <motion.path
            d={`${CHART_LINE} L296 110 L4 110 Z`}
            fill="url(#areaGrad)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 1.5 }}
          />
          <motion.path
            d={CHART_LINE}
            fill="none"
            stroke="url(#lineGrad)"
            strokeWidth="2.6"
            strokeLinecap="round"
            initial={reduced ? {} : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6, delay: 0.7, ease: EASE }}
          />
          {!reduced && (
            <circle r="3" fill="#2BD4C1">
              <animateMotion dur="7s" repeatCount="indefinite" path={CHART_LINE} />
            </circle>
          )}
          <circle cx="296" cy="12" r="3.5" fill="#0F9D77" />
        </svg>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
          {METERS.map((m, i) => (
            <div key={m.label}>
              <div className="flex items-center justify-between">
                <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-light">{m.label}</p>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-ice overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-royal via-teal-dark to-teal"
                  initial={reduced ? {} : { width: 0 }}
                  animate={{ width: `${m.v}%` }}
                  transition={{ duration: 1.1, delay: 1.2 + i * 0.12, ease: EASE }}
                />
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default function ProfitabilityEngine({ dark = false }) {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(null);
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const layerX = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const layerY = useTransform(sy, [-0.5, 0.5], [-5, 5]);
  const chipX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const chipY = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [4, -4]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-6, 6]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const chips = [
    { text: "Expense review", cls: "left-[0%] top-[38%]" },
    { text: "Cloud & connectivity", cls: "right-[0%] top-[34%]" },
    { text: "Virtual care", cls: "left-[0%] bottom-[30%]" },
    { text: "Credit eligibility", cls: "right-[0%] bottom-[26%]" },
  ];

  const activeNode = NODES.find((n) => n.key === active);

  return (
    <div
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative w-full max-w-[560px] mx-auto select-none [perspective:1300px]"
      data-testid="profitability-engine"
    >
      <motion.div
        className="relative w-full aspect-[10/9.4] [transform-style:preserve-3d]"
        style={reduced ? {} : { rotateX, rotateY }}
      >
        {/* faint orbital ring */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[74%] aspect-square rounded-full border border-white/[0.07]" aria-hidden="true" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[96%] aspect-square rounded-full border border-white/[0.05]" aria-hidden="true" />

        {/* floating context chips */}
        {!reduced &&
          chips.map((c, i) => (
            <motion.span
              key={c.text}
              style={reduced ? {} : { x: chipX, y: chipY, z: 70 }}
              className={`absolute ${c.cls} z-30 hidden lg:inline-flex items-center gap-1.5 backdrop-blur rounded-full pl-2.5 pr-3 py-1.5 text-[10px] font-bold shadow-lift border ${
                dark ? "bg-navy-800/80 border-white/10 text-teal-bright" : "bg-white/90 border-line text-royal"
              }`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1, transition: { delay: 1.3 + i * 0.14, duration: 0.5, ease: EASE } }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-teal" aria-hidden="true" />
              {c.text}
            </motion.span>
          ))}

        {/* connectors */}
        <motion.div
          className="absolute inset-0 z-0"
          style={reduced ? {} : { x: layerX, y: layerY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full" aria-hidden="true">
            {NODES.map((n) => (
              <g key={n.key}>
                <path
                  d={PATHS[n.key]}
                  fill="none"
                  stroke={active === n.key ? "#2BD4C1" : dark ? "#3D6BA8" : "#B9CFE4"}
                  strokeWidth={active === n.key ? 2.2 : 1.6}
                  vectorEffect="non-scaling-stroke"
                  style={{ transition: "stroke .3s, stroke-width .3s" }}
                />
              </g>
            ))}
          </svg>
        </motion.div>

        {/* core console */}
        <Core />

        {/* satellite panels */}
        {NODES.map((n, i) => (
          <div
            key={n.key}
            className={`absolute z-10 ${n.cls} -translate-x-1/2 -translate-y-1/2 w-[44%] sm:w-[33%]`}
          >
            <motion.div
              data-testid={`engine-node-${n.key}`}
              role="button"
              tabIndex={0}
              aria-label={`${n.label} — ${n.caption}`}
              onPointerEnter={() => setActive(n.key)}
              onPointerLeave={() => setActive(null)}
              onFocus={() => setActive(n.key)}
              onBlur={() => setActive(null)}
              initial={{ opacity: 0, scale: 0.7, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 160, damping: 18, delay: n.delay }}
            >
            <div
              className={`rounded-xl bg-white/[0.97] backdrop-blur border shadow-lift px-3 py-2.5 sm:px-3.5 sm:py-3 transition-all duration-300 cursor-pointer ${
                active === n.key ? "border-teal shadow-[0_0_0_3px_rgba(21,184,166,0.18),0_24px_48px_-16px_rgba(21,184,166,0.4)]" : "border-white/60"
              }`}
              style={{ animation: reduced ? undefined : `floatY ${5.5 + i * 0.7}s ease-in-out infinite`, animationDelay: `${i * 0.5}s` }}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg shrink-0 transition-colors ${active === n.key ? "bg-teal-soft" : "bg-ice"}`}>
                  <n.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-royal" strokeWidth={2} />
                </span>
                <span className="min-w-0">
                  <span className="block font-display font-extrabold tracking-tight text-navy text-[11px] sm:text-[12.5px] leading-snug">
                    {n.label}
                  </span>
                  <span className="block text-[8.5px] sm:text-[9.5px] font-semibold text-slate-light leading-tight truncate">
                    {n.sub}
                  </span>
                </span>
              </div>
              <Sparkline d={n.spark} active={active === n.key} />
            </div>
            </motion.div>
          </div>
        ))}
      </motion.div>

      <div className="mt-1 text-center" aria-live="polite">
        <motion.p
          key={active || "default"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`text-[13px] font-semibold ${dark ? "text-ice/60" : "text-slate"}`}
          data-testid="engine-caption"
        >
          {activeNode ? activeNode.caption : "Opportunities flow toward a healthier bottom line."}
        </motion.p>
      </div>
    </div>
  );
}
